/**
 * Token manager (security §2.1). Holds the ACCESS token IN MEMORY ONLY — never localStorage — and
 * auto-refreshes it shortly before expiry (web-client.md §6: timer keyed to exp − 60 s).
 *
 * The refresh token is NOT held here: it lives in an HttpOnly cookie the backend sets (security §2.1), so
 * `refreshNow()` just calls the endpoint with credentials and the browser presents the cookie. Only the
 * short-lived access token lives in this process, and only in memory.
 */
import { ProtocolError } from '../protocol/index.ts';
import type { Timers, TimerHandle } from '../net/timers.ts';
import { systemTimers } from '../net/timers.ts';
import type { AuthClient, AuthResponse } from './authClient.ts';

export interface TokenManagerOptions {
  authClient: AuthClient;
  timers?: Timers;
  /** Refresh this many ms before the access token expires (default 60 s). */
  skewMs?: number;
  /** Called with the new access token after every successful refresh (drives sys.reauth). */
  onAccessToken?: (accessToken: string) => void;
  onRefreshError?: (error: ProtocolError) => void;
}

export class TokenManager {
  private accessToken: string | null = null;
  private playerId: string | null = null;
  private expiresAtMs = 0;
  private refreshTimer: TimerHandle | null = null;

  private readonly authClient: AuthClient;
  private readonly t: Timers;
  private readonly skewMs: number;
  private readonly onAccessToken?: (accessToken: string) => void;
  private readonly onRefreshError?: (error: ProtocolError) => void;

  constructor(options: TokenManagerOptions) {
    this.authClient = options.authClient;
    this.t = options.timers ?? systemTimers;
    this.skewMs = options.skewMs ?? 60_000;
    this.onAccessToken = options.onAccessToken;
    this.onRefreshError = options.onRefreshError;
  }

  /** Adopt a fresh credential bundle and (re)schedule the pre-expiry refresh. */
  adopt(response: AuthResponse): void {
    this.accessToken = response.access;
    this.playerId = response.playerId;
    this.expiresAtMs = this.t.now() + response.expiresIn * 1000;
    this.scheduleRefresh(response.expiresIn * 1000);
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  getPlayerId(): string | null {
    return this.playerId;
  }

  /** Absolute expiry of the current access token, in ms since epoch (0 if none). */
  getExpiresAtMs(): number {
    return this.expiresAtMs;
  }

  isAuthenticated(): boolean {
    return this.accessToken !== null;
  }

  /** Refresh now (used by the pre-expiry timer and on a server AUTH_EXPIRED). Uses the HttpOnly refresh cookie. */
  async refreshNow(): Promise<void> {
    try {
      const next = await this.authClient.refresh();
      this.adopt(next);
      this.onAccessToken?.(next.access);
    } catch (error) {
      const pe =
        error instanceof ProtocolError
          ? error
          : new ProtocolError({ code: 'AUTH_INVALID', message: String(error), traceId: null });
      this.onRefreshError?.(pe);
      throw pe;
    }
  }

  clear(): void {
    this.accessToken = null;
    this.playerId = null;
    this.expiresAtMs = 0;
    this.t.clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
  }

  private scheduleRefresh(lifetimeMs: number): void {
    this.t.clearTimeout(this.refreshTimer);
    const delay = Math.max(0, lifetimeMs - this.skewMs);
    this.refreshTimer = this.t.setTimeout(() => {
      void this.refreshNow().catch(() => {
        /* onRefreshError already fired; the WS auth path will also react */
      });
    }, delay);
  }
}
